import { Guid } from "guid-typescript";
import { useMainStore } from "@/store";
import { BaseResponse } from "@/api/api";
import type { WsCallResp } from "@/api/wsrest";
import { WsRestAPI } from "@/api/api";
import { $request_ws } from "@/utils/request-ws";
import {
  WSREST_CALL_TIMEOUT_MS,
  WSREST_MAX_IN_FLIGHT,
  WSREST_PING_INTERVAL_MS,
  WSREST_RECONNECT_BASE_MS,
  WSREST_RECONNECT_MAX_MS,
} from "@/config/wsrest";

type PendingCall = {
  resolve: (resp: WsCallResp) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
};

type Waiter = () => void;

type GzipAssembly = {
  meta: WsCallResp;
  chunks: Map<number, ArrayBuffer>;
};

const state = {
  ws: undefined as WebSocket | undefined,
  pingTimer: undefined as ReturnType<typeof setInterval> | undefined,
  reconnectTimer: undefined as ReturnType<typeof setTimeout> | undefined,
  connectPromise: undefined as Promise<WebSocket> | undefined,
  pending: new Map<string, PendingCall>(),
  gzipPending: new Map<string, GzipAssembly>(),
  inFlight: 0,
  waitQueue: [] as Waiter[],
  reconnectAttempt: 0,
  intentionalClose: false,
};

const buildWsRestUrl = (token: string): string => {
  return (
    "wss://" +
    window.location.host +
    $request_ws.baseUrl +
    WsRestAPI.Gateway +
    "?token=" +
    encodeURIComponent(token)
  );
};

const drainWaitQueue = () => {
  while (state.inFlight < WSREST_MAX_IN_FLIGHT && state.waitQueue.length > 0) {
    const next = state.waitQueue.shift();
    next?.();
  }
};

const acquireInFlightSlot = (): Promise<void> => {
  if (state.inFlight < WSREST_MAX_IN_FLIGHT) {
    state.inFlight += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    state.waitQueue.push(() => {
      state.inFlight += 1;
      resolve();
    });
  });
};

const releaseInFlightSlot = () => {
  state.inFlight = Math.max(0, state.inFlight - 1);
  drainWaitQueue();
};

const clearPingTimer = () => {
  if (state.pingTimer != undefined) {
    clearInterval(state.pingTimer);
    state.pingTimer = undefined;
  }
};

const clearReconnectTimer = () => {
  if (state.reconnectTimer != undefined) {
    clearTimeout(state.reconnectTimer);
    state.reconnectTimer = undefined;
  }
};

const rejectAllPending = (message: string) => {
  for (const pending of state.pending.values()) {
    clearTimeout(pending.timer);
    pending.reject(new Error(message));
  }
  state.pending.clear();
  state.gzipPending.clear();
  state.waitQueue = [];
  state.inFlight = 0;
};

const resetConnectionState = () => {
  clearPingTimer();
  state.ws = undefined;
  state.connectPromise = undefined;
};

const startPingTimer = () => {
  clearPingTimer();
  state.pingTimer = setInterval(() => {
    if (state.ws?.readyState === WebSocket.OPEN) {
      state.ws.send("ping");
    }
  }, WSREST_PING_INTERVAL_MS);
};

function parseBinaryChunk(binary: ArrayBuffer): {
  reqId: string;
  chunkIndex: number;
  totalChunks: number;
  chunk: ArrayBuffer;
} {
  const view = new DataView(binary);
  const reqIdLen = view.getUint16(0);
  const reqId = new TextDecoder().decode(binary.slice(2, 2 + reqIdLen));
  const off = 2 + reqIdLen;
  return {
    reqId,
    chunkIndex: view.getUint16(off),
    totalChunks: view.getUint16(off + 2),
    chunk: binary.slice(off + 4),
  };
}

async function gunzipChunks(chunks: ArrayBuffer[]): Promise<unknown> {
  const ds = new DecompressionStream("gzip");
  const out = await new Response(
    new Blob(chunks).stream().pipeThrough(ds)
  ).arrayBuffer();
  return JSON.parse(new TextDecoder().decode(out));
}

const rejectPendingCall = (reqId: string, err: Error) => {
  const pending = state.pending.get(reqId);
  if (!pending) {
    return;
  }
  clearTimeout(pending.timer);
  state.pending.delete(reqId);
  state.gzipPending.delete(reqId);
  pending.reject(err);
};

const resolvePendingCall = (reqId: string, resp: WsCallResp) => {
  const pending = state.pending.get(reqId);
  if (!pending) {
    return;
  }
  clearTimeout(pending.timer);
  state.pending.delete(reqId);
  state.gzipPending.delete(reqId);
  pending.resolve(resp);
};

const tryCompleteGzip = async (reqId: string) => {
  const asm = state.gzipPending.get(reqId);
  if (!asm) {
    return;
  }
  const total = asm.meta.payload_chunks ?? 1;
  if (asm.chunks.size !== total) {
    return;
  }
  const ordered: ArrayBuffer[] = [];
  for (let i = 0; i < total; i++) {
    const chunk = asm.chunks.get(i);
    if (!chunk) {
      return;
    }
    ordered.push(chunk);
  }
  try {
    const data = await gunzipChunks(ordered);
    resolvePendingCall(reqId, { ...asm.meta, data });
  } catch (err) {
    rejectPendingCall(
      reqId,
      err instanceof Error ? err : new Error("gunzip failed")
    );
  }
};

const handleBinaryMessage = async (binary: ArrayBuffer) => {
  try {
    const { reqId, chunkIndex, totalChunks, chunk } = parseBinaryChunk(binary);
    const asm = state.gzipPending.get(reqId);
    if (!asm) {
      return;
    }
    const expected = asm.meta.payload_chunks ?? totalChunks;
    if (totalChunks !== expected) {
      rejectPendingCall(reqId, new Error("payload_chunks mismatch"));
      return;
    }
    asm.chunks.set(chunkIndex, chunk);
    await tryCompleteGzip(reqId);
  } catch {
    // ignore unrelated binary frames
  }
};

const handleTextMessage = (text: string) => {
  try {
    const resp = JSON.parse(text) as WsCallResp;
    const reqId = resp?.req_id;
    if (!reqId) {
      return;
    }
    if (resp.encoding === "gzip" && resp.data == null) {
      state.gzipPending.set(reqId, { meta: resp, chunks: new Map() });
      return;
    }
    resolvePendingCall(reqId, resp);
  } catch {
    // ignore unrelated frames
  }
};

const handleWsMessage = (evt: MessageEvent) => {
  if (evt.data === "pong") {
    return;
  }
  if (evt.data instanceof ArrayBuffer) {
    void handleBinaryMessage(evt.data);
    return;
  }
  handleTextMessage(evt.data as string);
};

const handleWsClose = (connectSettled: boolean) => {
  resetConnectionState();
  rejectAllPending("websocket closed");
  if (!connectSettled) {
    return;
  }
  if (!state.intentionalClose) {
    scheduleReconnect();
  }
};

const scheduleReconnect = () => {
  if (state.intentionalClose || state.reconnectTimer != undefined) {
    return;
  }
  const auth = useMainStore().token;
  if (auth == undefined) {
    return;
  }
  const delay = Math.min(
    WSREST_RECONNECT_BASE_MS * Math.pow(2, state.reconnectAttempt),
    WSREST_RECONNECT_MAX_MS
  );
  state.reconnectAttempt += 1;
  state.reconnectTimer = setTimeout(() => {
    state.reconnectTimer = undefined;
    void ensureConnected().catch(() => {
      scheduleReconnect();
    });
  }, delay);
};

const openWebSocket = (token: string): Promise<WebSocket> => {
  if (state.connectPromise != undefined) {
    return state.connectPromise;
  }

  state.connectPromise = new Promise((resolve, reject) => {
    const ws = new WebSocket(buildWsRestUrl(token));
    ws.binaryType = "arraybuffer";
    let connectSettled = false;

    ws.onopen = () => {
      connectSettled = true;
      state.intentionalClose = false;
      state.reconnectAttempt = 0;
      clearReconnectTimer();
      state.ws = ws;
      state.connectPromise = undefined;
      startPingTimer();
      resolve(ws);
    };

    ws.onmessage = handleWsMessage;

    ws.onclose = () => {
      if (!connectSettled) {
        state.connectPromise = undefined;
        handleWsClose(false);
        reject(new Error("websocket connect failed"));
        return;
      }
      handleWsClose(true);
    };
  });

  return state.connectPromise;
};

export const ensureConnected = async (): Promise<WebSocket> => {
  const auth = useMainStore().token;
  if (auth == undefined) {
    throw new Error("Permission denied");
  }
  const current = state.ws;
  if (current != undefined && current.readyState === WebSocket.OPEN) {
    return current;
  }
  return openWebSocket(auth);
};

export const callWsRest = async (
  api: string,
  body: Record<string, unknown>,
  timeoutMs: number = WSREST_CALL_TIMEOUT_MS
): Promise<WsCallResp> => {
  await acquireInFlightSlot();
  try {
    const ws = await ensureConnected();
    const reqId = Guid.create().toString();
    const normalizedApi = api.startsWith("/") ? api : `/${api}`;

    return await new Promise<WsCallResp>((resolve, reject) => {
      const timer = setTimeout(() => {
        state.pending.delete(reqId);
        state.gzipPending.delete(reqId);
        reject(new Error("wsrest call timeout"));
      }, timeoutMs);

      state.pending.set(reqId, { resolve, reject, timer });

      ws.send(
        JSON.stringify({
          type: "call",
          req_id: reqId,
          api: normalizedApi,
          body,
        })
      );
    });
  } finally {
    releaseInFlightSlot();
  }
};

export const closeWsRest = () => {
  state.intentionalClose = true;
  state.reconnectAttempt = 0;
  clearReconnectTimer();
  rejectAllPending("wsrest closed");
  const ws = state.ws;
  if (ws != undefined) {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send("close");
    }
    ws.close();
  }
  resetConnectionState();
};

export function isWsRestGatewayFallback(resp: WsCallResp): boolean {
  if (resp.success) {
    return false;
  }
  return resp.code === 403 || resp.code === 429 || resp.code === 502;
}

export function wsCallRespToBaseResponse(resp: WsCallResp): BaseResponse {
  return {
    code: resp.code,
    msg: resp.msg,
    success: resp.success,
    data: resp.data,
  };
}
